
import React, { useState, useEffect } from 'react';
import { Calendar, Download, MessageCircle } from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import Navbar from './Navbar';
import { checkCompanyWeekOff, calculateWorkingDays } from '../utils/weekendPolicy';

// Relative path — axiosInstance supplies the origin and the auth token.
const LOCAL_API_BASE_URL = '/api';

const EmployeeView = () => {
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const financialYearMonths = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];

  const currentDate = new Date();
  const currentCalendarYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const previousMonthIndex = currentMonthIndex === 0 ? 11 : currentMonthIndex - 1;
  const previousYear = currentMonthIndex === 0 ? currentCalendarYear - 1 : currentCalendarYear;
  const defaultMonthName = monthNames[previousMonthIndex];

  const [selectedMonth, setSelectedMonth] = useState(defaultMonthName);
  const [selectedYear, setSelectedYear] = useState(previousYear);
  const [employee, setEmployee] = useState(null);
  const [publicHolidays, setPublicHolidays] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [salarySlipGenerating, setSalarySlipGenerating] = useState(false);
  const [weekendPolicy, setWeekendPolicy] = useState('LAST_SATURDAY_OFF');

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUserId = localStorage.getItem("userId");

    if (!token || !storedUserId) {
      setError('Please login to access this page.');
      setLoading(false);
      return;
    }

    setUserId(storedUserId);

    const fetchPolicy = async () => {
      try {
        const res = await axiosInstance.get('/api/organization/weekend-policy');
        if (res.data?.status && res.data.data?.weekendPolicy) {
          setWeekendPolicy(res.data.data.weekendPolicy);
        }
      } catch (err) {
        console.error('Failed to fetch weekend policy:', err);
      }
    };
    fetchPolicy();
  }, []);

  const buildYearOptions = () => {
    const startYear = currentCalendarYear - 10;
    const endYear = currentCalendarYear + 50;
    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const availableYears = buildYearOptions();

  const getHolidayInfo = (date, month, year) =>
    publicHolidays[year]?.[month]?.[date] || null;

  const getMonthYear = (month) => {
    const monthIndex = financialYearMonths.indexOf(month);
    return monthIndex <= 8 ? selectedYear : selectedYear + 1;
  };

  const getMonthNumber = (monthName) => monthNames.indexOf(monthName) + 1;

  const getDaysInMonth = (month, year) => {
    const monthIndex = monthNames.indexOf(month);
    return new Date(year, monthIndex + 1, 0).getDate();
  };

  const getDayName = (date, month, year) => {
    const monthIndex = monthNames.indexOf(month);
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dateObj = new Date(year, monthIndex, date);
    return dayNames[dateObj.getDay()];
  };

  const isPublicHoliday = (date, month, year) => Boolean(getHolidayInfo(date, month, year));

  const isSunday = (date, month, year) => {
    return getDayName(date, month, year) === 'Sun';
  };
  const getWorkingDaysInMonth = (month, year) => {
    const totalDays = getDaysInMonth(month, year);
    let workingDays = 0;
    for (let day = 1; day <= totalDays; day++) {
      const weekOff = checkCompanyWeekOff(day, month, year, weekendPolicy);
      if (weekOff.isOff) continue;
      if (isPublicHoliday(day, month, year)) continue;
      workingDays++;
    }
    return workingDays;
  };

  const getPerDaySalary = (baseSalary, month, year) => {
    const workingDays = getWorkingDaysInMonth(month, year);
    return workingDays > 0 ? baseSalary / workingDays : 0;
  };

  const buildHolidayMap = (records = []) => {
    const map = {};
    records.forEach((holiday) => {
      const dateObj = new Date(holiday.date);
      if (Number.isNaN(dateObj.getTime())) return;
      const year = dateObj.getFullYear();
      const monthName = monthNames[dateObj.getMonth()];
      const day = dateObj.getDate();

      if (!map[year]) map[year] = {};
      if (!map[year][monthName]) map[year][monthName] = {};
      map[year][monthName][day] = {
        day,
        name: holiday.holiday_name || holiday.holidayName || holiday.name || 'Holiday',
        raw: holiday
      };
    });
    return map;
  };

  const buildDefaultAttendance = (monthName, year, isCurrentMonth = false) => {
    const daysInMonth = getDaysInMonth(monthName, year);
    return Array.from({ length: daysInMonth }, (_, index) => {
      const date = index + 1;
      const weekOff = checkCompanyWeekOff(date, monthName, year, weekendPolicy);
      if (weekOff.isOff) {
        return {
          date,
          status: weekOff.type === 'sunday' ? 'sunday' : 'holiday',
          dayName: getDayName(date, monthName, year),
          isHoliday: false,
          holidayName: weekOff.label
        };
      }
      const holidayInfo = getHolidayInfo(date, monthName, year);
      if (holidayInfo) {
        return {
          date,
          status: 'holiday',
          dayName: getDayName(date, monthName, year),
          isHoliday: true,
          holidayName: holidayInfo.name
        };
      }

      return {
        date,
        status: isCurrentMonth ? '' : 'present',
        dayName: getDayName(date, monthName, year),
        isHoliday: false
      };
    });
  };

  const transformApiAttendance = (records, monthName, year) => {
    return records
      .map((record) => {
        const rawDate = record.date || record.attendance_date;
        if (!rawDate) return null;
        const parsedDate = new Date(rawDate);
        if (Number.isNaN(parsedDate.getTime())) return null;
        const day = parsedDate.getDate();
        const status = (record.status || '').toLowerCase();
        const holidayInfo = getHolidayInfo(day, monthName, year);
        const recordHolidayName = record.holiday_name || record.holidayName || record.name;
        const finalHolidayName = recordHolidayName || holidayInfo?.name;
        const isHolidayDay = status === 'holiday' || Boolean(holidayInfo);
        return {
          date: day,
          status,
          dayName: getDayName(day, monthName, year),
          isHoliday: isHolidayDay,
          holidayName: isHolidayDay ? (finalHolidayName || 'Holiday') : undefined
        };
      })
      .filter(Boolean);
  };

  const fetchAttendanceFromApi = async (userId, month, year) => {
    try {
      const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/attendance`, {
        params: { userId, month, year }
      });
      const data = response?.data;
      if (Array.isArray(data?.data)) return data.data;
      if (Array.isArray(data?.attendance)) return data.attendance;
      return [];
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch attendance for user ${userId}:`, error.message || error);
      }
      return [];
    }
  };

  const fetchSalarySummaryFromApi = async (userId, month, year) => {
    try {
      const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/netsalary`, {
        params: { userId, month, year }
      });
      const rows = response?.data?.data;
      if (Array.isArray(rows) && rows.length > 0) {
        const latest = rows[0];
        const parseNumber = (value) => {
          if (value === undefined || value === null) return null;
          const num = parseFloat(value);
          return Number.isNaN(num) ? null : num;
        };
        return {
          netSalary: parseNumber(latest.net_salary ?? latest.netSalary),
          deduction: parseNumber(latest.salary_cut ?? latest.salaryCut),
          extraPay: parseNumber(latest.extra_pay ?? latest.extraPay),
          bonus: parseNumber(latest.bonus ?? latest.bonus),
          bonusReason: latest.bonus_reason ?? latest.bonusReason ?? null,
          perDaySalary: parseNumber(latest.per_day_salary ?? latest.perDaySalary),
          monthlySalary: parseNumber(latest.monthly_salary ?? latest.monthlySalary),
          raw: latest
        };
      }
      return null;
    } catch (error) {
      if (error?.response?.status !== 404) {
        console.error(`Failed to fetch salary summary for user ${userId}:`, error.message || error);
      }
      return null;
    }
  };

  const extractMonthlySalary = (response) => {
    const payload = response?.data;
    if (!payload) return null;

    const coerce = (value) => {
      if (value === undefined || value === null) return null;
      const num = parseFloat(value);
      return Number.isNaN(num) ? null : num;
    };

    if (typeof payload.monthlySalary === 'number' || typeof payload.monthlySalary === 'string') {
      return coerce(payload.monthlySalary);
    }

    if (typeof payload.monthly_salary === 'number' || typeof payload.monthly_salary === 'string') {
      return coerce(payload.monthly_salary);
    }

    if (payload.salary !== undefined) {
      if (typeof payload.salary === 'number' || typeof payload.salary === 'string') {
        return coerce(payload.salary);
      }
      const salaryObj = payload.salary || {};
      if (salaryObj.monthly_salary !== undefined) return coerce(salaryObj.monthly_salary);
      if (salaryObj.monthlySalary !== undefined) return coerce(salaryObj.monthlySalary);
    }

    if (Array.isArray(payload.data) && payload.data.length > 0) {
      const latestRecord = payload.data[0];
      if (latestRecord.monthly_salary !== undefined) return coerce(latestRecord.monthly_salary);
      if (latestRecord.monthlySalary !== undefined) return coerce(latestRecord.monthlySalary);
    }

    return null;
  };

  const calculateAttendanceStats = (attendance) => {
    let present = 0, cl = 0, pl = 0, absent = 0, sunday = 0, holiday = 0, extrapay = 0;

    attendance.forEach(day => {
      if (day.status === 'present') present++;
      else if (day.status === 'cl') cl++;
      else if (day.status === 'pl') pl++;
      else if (day.status === 'absent') absent++;
      else if (day.status === 'sunday') sunday++;
      else if (day.status === 'holiday') holiday++;
      else if (day.status === 'extrapay') extrapay++;
    });

    return { present, cl, pl, absent, sunday, holiday, extrapay };
  };

  const getQuarterFromMonth = (month) => {
    const quarterMap = {
      'January': 'Q1', 'February': 'Q1', 'March': 'Q1',
      'April': 'Q2', 'May': 'Q2', 'June': 'Q2',
      'July': 'Q3', 'August': 'Q3', 'September': 'Q3',
      'October': 'Q4', 'November': 'Q4', 'December': 'Q4',
    };
    return quarterMap[month] || 'Q1';
  };

  const getQuarterMonths = (month) => {
    const quarterMap = {
      'April': ['April', 'May', 'June'],
      'May': ['April', 'May', 'June'],
      'June': ['April', 'May', 'June'],
      'July': ['July', 'August', 'September'],
      'August': ['July', 'August', 'September'],
      'September': ['July', 'August', 'September'],
      'October': ['October', 'November', 'December'],
      'November': ['October', 'November', 'December'],
      'December': ['October', 'November', 'December'],
      'January': ['January', 'February', 'March'],
      'February': ['January', 'February', 'March'],
      'March': ['January', 'February', 'March'],
    };
    return quarterMap[month] || ['January', 'February', 'March'];
  };

  const getQuarterLeaveStatus = (monthlyData, month) => {
    const quarterMonths = getQuarterMonths(month);
    let totalCL = 0, totalPL = 0, totalUnpaid = 0;

    quarterMonths.forEach(m => {
      if (monthlyData[m]) {
        totalCL += monthlyData[m].cl || 0;
        totalPL += monthlyData[m].pl || 0;
        totalUnpaid += monthlyData[m].unpaid || 0;
      }
    });

    return { totalCL, totalPL, totalUnpaid, quarterMonths };
  };

  const calculateSalaryFromAttendance = (employeeData, month) => {
    const monthData = employeeData.monthlyData?.[month];
    if (!monthData) {
      return {
        netSalary: employeeData.baseSalary,
        deduction: 0,
        extraPay: 0,
        bonus: 0,
        present: 0,
        cl: 0,
        pl: 0,
        unpaid: 0,
        sunday: 0,
        holiday: 0,
        absent: 0,
        extrapay: 0
      };
    }

    const stats = calculateAttendanceStats(monthData.attendance || []);
    const quarterStatus = getQuarterLeaveStatus(employeeData.monthlyData || {}, month);

    let unpaidLeaves = 0;
    const totalPaidLeavesInQuarter = quarterStatus.totalCL + quarterStatus.totalPL;
    const quarterlyPaidLeaveLimit = 4;

    if (totalPaidLeavesInQuarter > quarterlyPaidLeaveLimit) {
      unpaidLeaves = totalPaidLeavesInQuarter - quarterlyPaidLeaveLimit;
    }
    unpaidLeaves += stats.absent;

    const netSalary = monthData.netSalary ?? employeeData.baseSalary;
    const deduction = monthData.deduction ?? monthData.serverSalary?.deduction ?? 0;
    const extraPay = monthData.extraPay ?? monthData.serverSalary?.extraPay ?? 0;
    const bonusValue = monthData.bonus ?? monthData.serverSalary?.bonus ?? 0;
    const bonus = parseFloat(bonusValue) || 0;

    return {
      netSalary,
      deduction,
      extraPay,
      bonus,
      present: stats.present,
      cl: stats.cl,
      pl: stats.pl,
      unpaid: unpaidLeaves,
      sunday: stats.sunday,
      holiday: stats.holiday,
      absent: stats.absent,
      extrapay: stats.extrapay
    };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-500';
      case 'cl': return 'bg-yellow-500';
      case 'pl': return 'bg-purple-500';
      case 'absent': return 'bg-red-500';
      case 'sunday': return 'bg-blue-500';
      case 'holiday': return 'bg-orange-500';
      case 'extrapay': return 'bg-amber-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'present': return 'P';
      case 'cl': return 'CL';
      case 'pl': return 'PL';
      case 'absent': return 'A';
      case 'sunday': return 'S';
      case 'holiday': return 'H';
      case 'extrapay': return 'EP';
      default: return '-';
    }
  };

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        const response = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/holiday`);
        const records = response.data?.data || [];
        setPublicHolidays(buildHolidayMap(records));
      } catch (err) {
        console.error('Failed to fetch holidays:', err);
      }
    };

    fetchHolidays();
  }, []);

  useEffect(() => {
    if (!userId) return;

    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        if (!token) return;

        // Fetch user details
        const userResponse = await axiosInstance.get(`/api/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${token.trim()}`
          }
        });

        if (!userResponse.data?.status || !userResponse.data?.data) {
          throw new Error('Failed to fetch user details');
        }

        const userData = userResponse.data.data;

        // Get base salary
        let baseSalary = userData.base_salary || 0;
        try {
          const salaryResponse = await axiosInstance.get(`${LOCAL_API_BASE_URL}/get/salary`, {
            params: { userId: userData.user_id_PK }
          });
          const savedSalary = extractMonthlySalary(salaryResponse);
          if (savedSalary) {
            baseSalary = parseFloat(savedSalary);
          }
        } catch (salaryErr) {
          if (salaryErr?.response?.status !== 404) {
            console.error(`Error fetching salary:`, salaryErr.message);
          }
        }

        const monthNumber = getMonthNumber(selectedMonth);
        const year = getMonthYear(selectedMonth);

        // Fetch attendance
        const apiRecords = await fetchAttendanceFromApi(userId, monthNumber, year);
        const isCurrentMonth = monthNumber === new Date().getMonth() + 1 && year === new Date().getFullYear();

        const attendanceEntries = apiRecords.length
          ? transformApiAttendance(apiRecords, selectedMonth, year)
          : buildDefaultAttendance(selectedMonth, year, isCurrentMonth);

        const stats = calculateAttendanceStats(attendanceEntries);

        // Fetch salary summary
        const salarySummary = await fetchSalarySummaryFromApi(userId, monthNumber, year);

        const parseSafe = (value, fallback = 0) => {
          if (value === null || value === undefined) return fallback;
          const parsed = parseFloat(value);
          return Number.isNaN(parsed) ? fallback : parsed;
        };

        const finalNetSalary = salarySummary?.netSalary ?? baseSalary;
        const finalDeduction = salarySummary?.deduction ?? 0;
        const finalExtraPay = salarySummary?.extraPay ?? 0;
        const finalBonus = parseSafe(salarySummary?.bonus, 0);

        const employeeData = {
          id: userData.user_id_PK,
          name: `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || 'Unknown',
          avatar: userData.user_profile_img,
          baseSalary,
          email: userData.email,
          designation: userData.designation_name,
          department: userData.department_name,
          monthlyData: {
            [selectedMonth]: {
              attendance: attendanceEntries,
              present: stats.present,
              cl: stats.cl,
              pl: stats.pl,
              absent: stats.absent,
              sunday: stats.sunday,
              holiday: stats.holiday,
              netSalary: finalNetSalary,
              deduction: finalDeduction,
              extraPay: finalExtraPay,
              bonus: finalBonus,
              serverSalary: salarySummary || {
                netSalary: finalNetSalary,
                deduction: finalDeduction,
                extraPay: finalExtraPay,
                bonus: finalBonus,
                perDaySalary: salarySummary?.perDaySalary,
                monthlySalary: salarySummary?.monthlySalary || baseSalary
              }
            }
          }
        };

        setEmployee(employeeData);
      } catch (err) {
        console.error('Error fetching employee data:', err);
        setError(err.response?.data?.message || 'Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [userId, selectedMonth, selectedYear, publicHolidays]);

  const generateSalarySlip = async () => {
    if (!employee) return;

    const year = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);

    if (monthNumber < 1) {
      alert('Invalid month selected.');
      return;
    }

    setSalarySlipGenerating(true);

    try {
      const response = await axiosInstance.post(
        `${LOCAL_API_BASE_URL}/downloadsalaryslip/${employee.id}`,
        {
          month: monthNumber,
          year: year
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.status && response.data.filePath) {
        window.open(response.data.filePath, '_blank');
      } else {
        alert(response.data?.message || 'Failed to generate salary slip');
      }
    } catch (error) {
      console.error('Error generating salary slip:', error);
      const errorMessage = error.response?.data?.message || 'Failed to generate salary slip. Please try again.';
      alert(errorMessage);
    } finally {
      setSalarySlipGenerating(false);
    }
  };

  const shareSalarySlipWhatsapp = async () => {
    if (!employee || !employee.id) return;

    const year = getMonthYear(selectedMonth);
    const monthNumber = getMonthNumber(selectedMonth);

    if (monthNumber < 1) {
      alert('Invalid month selected.');
      return;
    }

    setSalarySlipGenerating(true);

    try {
      const response = await axiosInstance.post(
        `${LOCAL_API_BASE_URL}/downloadsalaryslip/${employee.id}`,
        {
          month: monthNumber,
          year: year
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.status && response.data.whatsappUrl) {
        window.open(response.data.whatsappUrl, '_blank');
      } else if (response.data?.filePath) {
        window.open(response.data.filePath, '_blank');
      } else {
        alert(response.data?.message || 'Failed to generate salary slip');
      }
    } catch (error) {
      console.error('Error sharing salary slip on WhatsApp:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to prepare WhatsApp slip. Please try again.';
      alert(errorMessage);
    } finally {
      setSalarySlipGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-red-600 font-semibold mb-4">{error}</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <p className="text-gray-600">No employee data found</p>
        </div>
      </div>
    );
  }

  const monthData = employee.monthlyData[selectedMonth];
  const { netSalary, deduction, extraPay, bonus, present, cl, pl, unpaid, sunday, holiday, absent } = calculateSalaryFromAttendance(employee, selectedMonth);
  const year = getMonthYear(selectedMonth);
  const workingDays = getWorkingDaysInMonth(selectedMonth, year);
  const perDaySalary = getPerDaySalary(employee.baseSalary, selectedMonth, year);
  const quarterStatus = getQuarterLeaveStatus(employee.monthlyData || {}, selectedMonth);
  const displayNetSalary = monthData?.netSalary ?? monthData?.serverSalary?.netSalary ?? netSalary ?? employee.baseSalary;
  const weekOrder = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const groupedByDay = weekOrder.reduce((acc, day) => {
    acc[day] = monthData?.attendance?.filter(
      (item) => item.dayName === day
    ) || [];
    return acc;
  }, {});


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navbar />
      <div className="p-6">
        <div className="max-w-7xl mx-auto">




          {/* My Attendance Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">

                {/* Left Section */}
                <div className="flex items-center gap-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                    <Calendar size={32} />
                  </div>
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-1">
                      My Attendance
                    </h1>
                    <p className="text-blue-100">
                      View your monthly attendance & salary slip
                    </p>
                  </div>
                </div>

                {/* Right Section */}
                <div className="flex flex-wrap items-center gap-4">
                  <button
                    onClick={generateSalarySlip}
                    disabled={salarySlipGenerating}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold shadow-md transition-colors text-sm
          ${salarySlipGenerating
                        ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                      }`}
                  >
                    <Download size={16} />
                    {salarySlipGenerating ? 'Generating...' : 'Slip PDF'}
                  </button>
                  <button
                    onClick={shareSalarySlipWhatsapp}
                    disabled={salarySlipGenerating}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold shadow-md transition-colors text-sm
          ${salarySlipGenerating
                        ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
                        : 'bg-[#25D366] hover:bg-[#1ebd5a] text-white'
                      }`}
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </button>
                </div>

              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-4">
                <Calendar className="text-blue-600" size={20} />
                <span>Select Month & Year</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Month</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-300 cursor-pointer"
                  >
                    {financialYearMonths.map((month) => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-2">Year</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-white hover:border-gray-300 cursor-pointer"
                  >
                    {availableYears.map((yearOption) => (
                      <option key={yearOption} value={yearOption}>
                        {yearOption}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>


            {/* Attendance Calendar View */}
            {monthData && monthData.attendance && (
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-600 mb-3">
                  {selectedMonth} {getMonthYear(selectedMonth)} - Attendance Calendar
                </p>

                {/* Week Header */}
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {weekOrder.map((day) => (
                    <div
                      key={day}
                      className={`text-center font-bold text-sm ${day === "Sun" ? "text-red-600" : "text-gray-700"
                        }`}
                    >
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Body */}
                <div className="grid grid-cols-7 gap-3">
                  {weekOrder.map((day) => (
                    <div key={day} className="flex flex-col gap-2">
                      {groupedByDay[day].map((item) => (
                        <div
                          key={item.date}
                          className={`${getStatusColor(item.status)} text-white text-xs rounded-lg p-2 text-center font-semibold shadow`}
                          title={`${item.date} ${selectedMonth} (${item.dayName})`}
                        >
                          <div className="text-sm">{item.date}</div>
                          <div className="text-[10px]">
                            {getStatusLabel(item.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Base Salary</p>
                <p className="text-lg font-bold text-blue-600">₹{employee.baseSalary.toLocaleString()}</p>
              </div>
              {extraPay > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1">Extra Pay</p>
                  <p className="text-lg font-bold text-amber-600">₹{extraPay.toLocaleString()}</p>
                </div>
              )}
              <div className="bg-green-50 p-3 rounded-lg">
                <p className="text-xs text-gray-600 mb-1">Net Salary</p>
                <p className="text-lg font-bold text-green-600">
                  ₹{displayNetSalary.toLocaleString()}
                  {bonus > 0 && (
                    <span
                      className="text-sm font-normal text-green-700 ml-2 cursor-help"
                      title={monthData?.serverSalary?.bonusReason
                        ? `${monthData.serverSalary.bonusReason} `
                        : monthData?.bonusReason
                          ? `${monthData.bonusReason} `
                          : `Bonus `}
                    >
                      (incl. Bonus: ₹{bonus.toLocaleString()})
                    </span>
                  )}
                </p>
                {(bonus > 0 || extraPay > 0) && (
                  // Net from the server already includes extra pay and bonus (same as the payslip)
                  <p className="text-xs text-gray-500 mt-1">
                    Includes{extraPay > 0 ? ` extra pay ₹${extraPay.toLocaleString()}` : ''}{extraPay > 0 && bonus > 0 ? ' +' : ''}{bonus > 0 ? ` bonus ₹${bonus.toLocaleString()}` : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Attendance Stats */}
            <div className="grid grid-cols-6 gap-2 mb-4">
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Present</p>
                <p className="text-xl font-bold text-green-600">{present}</p>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">CL</p>
                <p className="text-xl font-bold text-yellow-600">{cl}</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">PL</p>
                <p className="text-xl font-bold text-purple-600">{pl}</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Absent</p>
                <p className="text-xl font-bold text-red-600">{absent}</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Sunday</p>
                <p className="text-xl font-bold text-blue-600">{sunday}</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg text-center">
                <p className="text-xs text-gray-600">Holiday</p>
                <p className="text-xl font-bold text-orange-600">{holiday}</p>
              </div>
            </div>

            {/* Deduction Warning */}
            {deduction > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-red-700 font-semibold">
                  ⚠️ Salary Deduction: ₹{deduction.toLocaleString()} ({unpaid} unpaid days)
                </p>
                <p className="text-xs text-red-600 mt-1">
                  • {absent} direct absent days + {Math.max(0, (quarterStatus.totalCL + quarterStatus.totalPL - 4))} excess quarterly leaves
                </p>
              </div>
            )}

            {/* Quarter Status */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-amber-800">
                  Quarter Status ({quarterStatus.quarterMonths.map((month, idx) => (
                    <span key={month}>
                      {month}
                      {idx < quarterStatus.quarterMonths.length - 1 && ', '}
                    </span>
                  ))})
                </p>
              </div>
              <div className="flex gap-4 text-xs flex-wrap mb-2">
                <span className="text-gray-700">Total CL: <strong>{quarterStatus.totalCL}</strong></span>
                <span className="text-gray-700">Total PL: <strong>{quarterStatus.totalPL}</strong></span>
                <span className="text-gray-700">Total Paid: <strong>{quarterStatus.totalCL + quarterStatus.totalPL}/4</strong></span>
                <span className="text-gray-700">Unpaid: <strong className="text-red-600">{quarterStatus.totalUnpaid}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeView;
